import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, vModelRadio as _vModelRadio, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-6" }
const _hoisted_2 = {
  key: 0,
  class: "flex gap-4"
}
const _hoisted_3 = { class: "text-gray-900 text-lg font-medium capitalize" }
const _hoisted_4 = { class: "flex flex-col gap-3" }
const _hoisted_5 = { class: "flex items-center" }
const _hoisted_6 = ["id", "value"]
const _hoisted_7 = ["for"]
const _hoisted_8 = {
  key: 0,
  class: "flex flex-col gap-y-2"
}
const _hoisted_9 = { class: "flex gap-x-2 w-full mt-3" }
const _hoisted_10 = {
  key: 1,
  class: "mt-4"
}

import { computed, onMounted, ref, watch } from "vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import FilterTag from "@/components/filters/FilterTag.vue"
import FilterCollection from "@/components/filters/FilterCollection.vue"
import GgmsCheckBoxList from "@/components/GgmsCheckBoxList.vue"
import GgmsRadioSelect from "@/components/GgmsRadioSelect.vue"
import GgmsCalendar from "@/components/GgmsCalendar.vue"
import FilterOrigin from "@/components/filters/FilterOrigin.vue"
import { ArrowLeftIcon } from "@heroicons/vue/outline"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useTagStore } from "@/stores/tag"
import { useOriginStore } from "@/stores/origin"
import { Filter } from "@/shared/models/filter"
import { displayFilterRule, getTaskTypes, formatDateToIso, formatDateToUtc } from "@/shared/utils/helpers"

interface Props {
    filter: Filter
    isUpdated?: boolean
    showHeader?: boolean
    showButton?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterRule',
  props: {
    filter: {},
    isUpdated: { type: Boolean },
    showHeader: { type: Boolean, default: true },
    showButton: { type: Boolean, default: true }
  },
  emits: ["applyFilter", "cancel"],
  setup(__props: any, { emit: __emit }) {

const tagStore = useTagStore()
const originStore = useOriginStore()
const gridCollectionStore = useGridCollectionStore()

const props = __props
const emit = __emit

const localFilter = ref<Filter>({
    field: props.filter.field,
    type: props.filter.type,
    rule: props.filter.rule,
    value: props.filter.value,
    displayName: props.filter.displayName,
})
const localFilterTags = ref({
    field: "tags._id",
    type: "string[]",
    rule: "isInIds",
    value: [] as string[],
    displayName: "Tags",
})

const localFilterOrigins = ref({
    field: "origins._id",
    type: "string[]",
    rule: "isInIds",
    value: [] as string[],
    displayName: "Origins",
})

const localFilterCollections = ref({
    field: "lists._id",
    type: "string[]",
    rule: "isInIds",
    value: [] as string[],
    displayName: "Lists",
})

const multipleModelValues = ref<boolean[]>([])
const customFields = ["birthday", "purchaseAnniversary"]

const statusOptions = [
    { displayName: "Pending", value: "pending" },
    { displayName: "Completed", value: "completed" },
]

const workflowStatusOptions = [
    { displayName: "On", value: "on" },
    { displayName: "Off", value: "off" },
    { displayName: "Unpublished", value: "unpublished" },
]

const agentStatusOptions = [
    { displayName: "Active", value: "active" },
    { displayName: "Pending", value: "pending" },
    { displayName: "Disabled", value: "disabled" },
]

const inviteStatusOptions = [
    { displayName: "Accepted", value: "accepted" },
    { displayName: "Pending", value: "pending" },
]

const shouldRemoveOptions = [
    { displayName: "Yes", value: "true" },
    { displayName: "No", value: "false" },
]

const reminderOptions = [
    { displayName: "30 Minutes Before", value: "minutes_30" },
    { displayName: "1 Hour Before", value: "hours_1" },
    { displayName: "1 Day Before", value: "days_1" },
    { displayName: "1 Week Before", value: "weeks_1" },
]

const stateOptions = [
    { displayName: "Published", value: "Published" },
    { displayName: "Draft", value: "Draft" },
]

const roleOptions = [
    { displayName: "Owner", value: "owner" },
    { displayName: "Member", value: "member" },
]

const visiblityOptions = [
    { displayName: "Visible", value: "Visible" },
    { displayName: "Hidden", value: "Hidden" },
]

const lockedOptions = [
    { displayName: "Unlocked", value: "Unlocked" },
    { displayName: "Locked", value: "Locked" },
]

const usagesOptions = [
    { displayName: "Has Usages", value: "Has Usages" },
    { displayName: "No Usages", value: "No Usages" },
]

const showTime = computed(() => {
    return !customFields.some((field) => localFilter.value.field.endsWith(field))
})

const filterButtonText = computed(() => (props.isUpdated ? "Update filter" : "Apply filter"))

const isBetweenRule = computed(() => ["isBetween", "isNotBetween"].includes(localFilter.value.rule as string))
const isDateTypeFilter = computed(() => ["date", "eventtime"].includes(localFilter.value.type?.toLowerCase() as string))
const isNumberTypeFilter = computed(() => localFilter.value.type?.toLowerCase() === "number")
const isNotCompareableAttribute = computed(() => ["isEmpty", "isNotEmpty"].includes(localFilter.value.rule as string))
const localFilterInputType = computed(() => {
    if (isNumberTypeFilter.value) {
        return "number"
    }
    return "text"
})
const localFilterStart = ref(new Date((props.filter.value as Date[])?.[0] || ""))
const localFilterEnd = ref(new Date((props.filter.value as Date[])?.[1] || ""))

const hasFilter = computed(() => !!filterRules.value?.length)
const tagsSelectedState = ref<boolean[]>([])
const originsSelectedState = ref<boolean[]>([])
const collectionsSelectedState = ref<boolean[]>([])
const collectionType = computed(() => gridCollectionStore.collectionType.replace(/s$/, ""))

const typeFilterOptions = ref(setFilterTypeOptions())

const filterRules = computed(() => {
    let rules: string[] = []

    if (
        ["type", "shouldRemoveIfNotCompleted", "lists._id", "isHidden", "isLocked", "isUsed"].includes(
            localFilter.value.field
        ) ||
        localFilter.value.type?.toLowerCase() === "array"
    ) {
        return rules
    }

    if (localFilter.value.type?.toLowerCase() === "object") {
        switch (localFilter.value.field) {
            case "when.startedAt":
                rules = ["isLessThan", "isGreaterThan"]
                break
            case "agent.fullName":
            case "phoneNumber.number":
            case "phone.number":
            case "collection.displayName":
                rules = ["isEmpty", "isNotEmpty", "contains", "doesNotContain", "isEqualTo", "isNotEqualTo"]
                break
        }

        return rules
    }

    switch (localFilter.value.type?.toLowerCase()) {
        case "email":
        case "phonenumber":
        case "string":
            rules = ["isEmpty", "isNotEmpty", "contains", "doesNotContain", "isEqualTo", "isNotEqualTo"]
            break
        case "number":
            rules = [
                "isEmpty",
                "isNotEmpty",
                "isEqualTo",
                "isNotEqualTo",
                "isLessThan",
                "isGreaterThan",
                "isBetween",
                "isNotBetween",
            ]
            break
        case "date":
        case "eventtime":
            rules = ["isEqualTo", "isLessThan", "isGreaterThan", "isBetween", "isNotBetween"]
            break
        case "email[]":
            rules = ["isEmpty", "isNotEmpty", "contains", "doesNotContain"]
            break
        case "phonenumber[]":
            rules = ["isEmpty", "isNotEmpty", "contains", "doesNotContain"]
            break
    }
    return rules
})

const isFilterValid = computed(() => {
    if (
        localFilter.value.field === "shouldRemoveIfNotCompleted" ||
        localFilterTags.value.value?.length ||
        localFilterOrigins.value.value?.length ||
        localFilterCollections.value.value?.length
    ) {
        return true
    }

    if (!localFilter.value.rule) {
        return false
    }

    return !(
        ["contains", "doesNotContain", "isLessThan", "isGreaterThan", "isBetween"].includes(
            localFilter.value.rule || ""
        ) && !localFilter.value.value
    )
})

function getFilterValue() {
    if (localFilterTags.value.value?.length) {
        return localFilterTags.value
    }
    if (localFilterOrigins.value.value?.length) {
        return localFilterOrigins.value
    }
    if (localFilterCollections.value.value?.length) {
        return localFilterCollections.value
    }
    return localFilter.value
}

// Define transformations for specific fields
const fieldTransformations = {
    // forms
    state: () => {
        localFilter.value.field = "isDraft"
        localFilter.value.type = "state"
    },
    //agents
    role: () => {
        localFilter.value.field = "invites?.role.name"
    },
    status: () => {
        if (gridCollectionStore.collectionType === "agents") {
            localFilter.value.field = "invites.status"
        }
        if (gridCollectionStore.collectionType === "workflows") {
            localFilter.value.field = "status"
        }
    },
    inviteStatus: () => {
        localFilter.value.field = "invites.inviteStatus"
    },
    email: () => {
        if (collectionType.value !== "agent") {
            localFilter.value.field = "email.emailAddress"
        }
    },
    phoneNumber: () => {
        localFilter.value.field = "phoneNumber.number"
    },
}

function applyDateFilter() {
    localFilter.value.value = isBetweenRule.value
        ? Array.isArray(localFilter.value.value)
            ? localFilter.value.value.map((date) => {
                  // Check if the date is undefined, null, or an empty string
                  if (!date) {
                      // If it is, return an empty string
                      return ""
                  } else {
                      // If it's not, format the date as before
                      return hasTime(date as string) ? formatDateToUtc(date) : formatDateWithoutTime(date as string)
                  }
              })
            : []
        : hasTime(localFilter.value.value?.toString() ?? "")
        ? formatDateToUtc(localFilter.value.value?.toString() ?? "")
        : formatDateWithoutTime(localFilter.value.value?.toString() ?? "")
}

// Check if a date string has a time component
function hasTime(dateString: string) {
    // Split the date string on space or 'T' to get the time part
    const timePart = dateString.split(" ")[1] || dateString.split("T")[1]
    // Check if the time part exists and is not '00:00:00'
    return timePart && timePart !== "00:00:00"
}

// Format a date string without a time component
function formatDateWithoutTime(dateString: string) {
    // Split the date string on '-' to get the year, month, and day parts
    const [year, month, day] = dateString.split("-").map(Number)
    // Return a date string in the format 'YYYY-MM-DDT00:00:00'
    return `${year}-${pad(month)}-${pad(day)}T00:00:00`
}

// Pad single digit numbers with a leading zero
function pad(number: number) {
    // If the number is less than 10, prepend a '0'. Otherwise, return the number as is.
    return number < 10 ? "0" + number : number
}

// Apply number filter transformation
function applyNumberFilter() {
    // If the rule is 'between', map each value to a number
    // Otherwise, just convert the single value to a number
    localFilter.value.value = isBetweenRule.value
        ? (localFilter.value.value as string[])?.map((value) => Number(value).toString())
        : Number(localFilter.value.value).toString()
}

// Apply boolean filter transformation
function applyBooleanFilter() {
    // By default, set the rule to 'isEqualTo'
    localFilter.value.rule = "isEqualTo"
    // If the field is 'isHidden', 'isLocked', or 'isUsed', set the rule to 'isIn'
    if (["isHidden", "isLocked", "isUsed"].includes(localFilter.value.field)) {
        localFilter.value.rule = "isIn"
    }
}

// Apply the appropriate filter based on the type and field
function applyFilter() {
    // If the rule is 'between', apply the date or number filter as appropriate
    if (isBetweenRule.value) {
        if (localFilter.value.type === "Date") {
            applyDateFilter()
        } else if (localFilter.value.type === "number") {
            applyNumberFilter()
        }
    } else {
        // If the rule is not 'between', apply the appropriate filter based on the type
        if (localFilter.value.type === "Date") {
            applyDateFilter()
        } else if (localFilter.value.type === "number") {
            applyNumberFilter()
        } else if (localFilter.value.type === "boolean") {
            applyBooleanFilter()
        } else {
            // If the type is not 'Date', 'number', or 'boolean', apply the field transformation if one exists
            const transform = fieldTransformations[localFilter.value.field]
            if (transform) {
                transform()
            }
        }
    }

    const filterValue = getFilterValue()

    if (gridCollectionStore.collectionType === "listings" && !filterValue.field.startsWith("attributes.")) {
        filterValue.field = `attributes.${filterValue.field}`
    }

    if (gridCollectionStore.collectionType === "templates" && filterValue.field === "type") {
        filterValue.field = "templateType"
    }

    emit("applyFilter", filterValue)
}

function cancelFilter() {
    emit("cancel")
}

function onTagSelect(selectedTagsIds: string[]) {
    localFilterTags.value.value = Array.from(selectedTagsIds)
}

function onOriginSelect(selectedOriginsIds: string[]) {
    localFilterOrigins.value.value = Array.from(selectedOriginsIds)
}

function onCollectionSelect(selectedCollectionsIds: string[]) {
    localFilterCollections.value.value = Array.from(selectedCollectionsIds)
}

function changeLocalFilterValueType() {
    if (localFilter.value.type === "Date") {
        const today = new Date()
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)

        localFilter.value.value = isBetweenRule.value ? [today, tomorrow] : today
        localFilterStart.value = today
        localFilterEnd.value = tomorrow
        return
    }

    localFilter.value.value = isBetweenRule.value ? ["", ""] : ""
}

function setOptions(options, filterValue) {
    multipleModelValues.value = filterValue?.length
        ? options.map((option) => filterValue.includes(option.value))
        : new Array(options.length).fill(false)

    return options
}

function setFilterTypeOptions() {
    if (collectionType.value === "task") {
        if (localFilter.value.field === "status") {
            return setOptions(statusOptions, props.filter.value)
        }

        const taskOptions = getTaskTypes().map((type) => ({ displayName: type.value, value: type.attribute }))
        return setOptions(taskOptions, props.filter.value)
    }

    if (collectionType.value === "workflow") {
        if (localFilter.value.field === "status") {
            return setOptions(workflowStatusOptions, props.filter.value)
        }
    }

    if (collectionType.value === "tag") {
        if (localFilter.value.field === "isHidden") {
            return setOptions(visiblityOptions, props.filter.value)
        }

        if (localFilter.value.field === "isLocked") {
            return setOptions(lockedOptions, props.filter.value)
        }

        if (localFilter.value.field === "isUsed") {
            return setOptions(usagesOptions, props.filter.value)
        }

        const tagTypesOptions = tagStore.tagTypes.map((tag: { label: string; value: string }) => ({
            displayName: tag.label,
            value: tag.value,
        }))
        return setOptions(tagTypesOptions, props.filter.value)
    }

    if (collectionType.value === "form") {
        return setOptions(stateOptions, props.filter.value)
    }

    if (collectionType.value === "agent") {
        if (["status", "invites.status"].includes(localFilter.value.field)) {
            return setOptions(agentStatusOptions, props.filter.value)
        }

        return setOptions(roleOptions, props.filter.value)
    }

    if (collectionType.value === "messaging-template" || collectionType.value === "template") {
        const templateTypesOptions = [
            { displayName: "Text Message", value: "sms" },
            { displayName: "Email", value: "email" },
        ]
        return setOptions(templateTypesOptions, props.filter.value)
    }

    return []
}

function setLocalFilterValue(value: boolean[]) {
    const selectedTypes = [] as string[]
    value.forEach((isSelected, index) => {
        if (isSelected) {
            selectedTypes.push(typeFilterOptions.value[index].value)
        }
    })

    localFilter.value.value = Array.from(selectedTypes)
    localFilter.value.rule = "isIn"
}

function onItemSelect(value: boolean) {
    localFilter.value.value = value.toString()
    localFilter.value.rule = "isEqualTo"
}

onMounted(() => {
    if (props.filter.field === "tags._id") {
        localFilterTags.value = structuredClone(props.filter)
        tagsSelectedState.value = tagStore.tableState.data.map((tag) => localFilterTags.value.value.includes(tag._id))
        return
    }
    if (props.filter.field === "origins._id") {
        localFilterOrigins.value = structuredClone(props.filter)
        originsSelectedState.value = originStore.tableState.data.map((origin) =>
            localFilterOrigins.value.value.includes(origin._id)
        )
        return
    }
    if (props.filter.field === "lists._id") {
        localFilterCollections.value = structuredClone(props.filter)
        localFilterCollections.value.rule = "isInIds"
        collectionsSelectedState.value = gridCollectionStore.collectionTableState.data.map((collection) =>
            localFilterCollections.value.value.includes(collection._id)
        )
        return
    }
})

watch(localFilterStart, (newVal) => {
    if (newVal) {
        localFilter.value.value[0] = newVal.toString()
    }
})

watch(localFilterEnd, (newVal) => {
    if (newVal) {
        localFilter.value.value[1] = newVal.toString()
    }
})

watch(
    () => multipleModelValues.value,
    (newVal) => {
        setLocalFilterValue(newVal)
    },
    { deep: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.showHeader)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(ArrowLeftIcon), {
            class: "h-6 w-6 text-primary-color-700 cursor-pointer shrink-0",
            onClick: _withModifiers(cancelFilter, ["stop"])
          }),
          _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.filter.displayName), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (['status', 'invites.status'].includes(localFilter.value.field))
        ? (_openBlock(), _createBlock(GgmsCheckBoxList, {
            key: 0,
            modelValue: multipleModelValues.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((multipleModelValues).value = $event)),
            options: 
                    collectionType.value === 'agent'
                        ? agentStatusOptions
                        : collectionType.value === 'workflow'
                        ? workflowStatusOptions
                        : statusOptions
                
          }, null, 8, ["modelValue", "options"]))
        : (['inviteStatus', 'invites.inviteStatus'].includes(localFilter.value.field))
          ? (_openBlock(), _createBlock(GgmsRadioSelect, {
              key: 1,
              modelValue: localFilter.value.value,
              "onUpdate:modelValue": [
                _cache[1] || (_cache[1] = ($event: any) => ((localFilter.value.value) = $event)),
                onItemSelect
              ],
              options: inviteStatusOptions
            }, null, 8, ["modelValue"]))
          : (localFilter.value.field === 'shouldRemoveIfNotCompleted')
            ? (_openBlock(), _createBlock(GgmsRadioSelect, {
                key: 2,
                modelValue: localFilter.value.value,
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => ((localFilter.value.value) = $event)),
                  onItemSelect
                ],
                options: shouldRemoveOptions
              }, null, 8, ["modelValue"]))
            : (localFilter.value.field === 'reminder')
              ? (_openBlock(), _createBlock(GgmsRadioSelect, {
                  key: 3,
                  modelValue: localFilter.value.value,
                  "onUpdate:modelValue": [
                    _cache[3] || (_cache[3] = ($event: any) => ((localFilter.value.value) = $event)),
                    onItemSelect
                  ],
                  options: reminderOptions
                }, null, 8, ["modelValue"]))
              : (['state', 'isDraft'].includes(localFilter.value.field))
                ? (_openBlock(), _createBlock(GgmsCheckBoxList, {
                    key: 4,
                    modelValue: multipleModelValues.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((multipleModelValues).value = $event)),
                    options: stateOptions
                  }, null, 8, ["modelValue"]))
                : (
                    [
                        'role',
                        'invites?.role.name',
                        'type',
                        'templateType',
                        'typeName',
                        'isHidden',
                        'isLocked',
                        'isUsed',
                    ].includes(localFilter.value.field)
                )
                  ? (_openBlock(), _createBlock(GgmsCheckBoxList, {
                      key: 5,
                      modelValue: multipleModelValues.value,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((multipleModelValues).value = $event)),
                      options: typeFilterOptions.value
                    }, null, 8, ["modelValue", "options"]))
                  : (filterRules.value?.length)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 6 }, _renderList(filterRules.value, (filterRule, index) => {
                        return (_openBlock(), _createElementBlock("div", { key: index }, [
                          _createElementVNode("div", _hoisted_5, [
                            _withDirectives(_createElementVNode("input", {
                              id: filterRule,
                              value: filterRule,
                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localFilter.value.rule) = $event)),
                              name: "notification-method",
                              type: "radio",
                              class: "focus:ring-primary-color-500 h-4 w-4 text-primary-color-600 border-gray-300",
                              onChange: _cache[7] || (_cache[7] = ($event: any) => (changeLocalFilterValueType()))
                            }, null, 40, _hoisted_6), [
                              [_vModelRadio, localFilter.value.rule]
                            ]),
                            _createElementVNode("label", {
                              for: filterRule,
                              class: "ml-3 block text-sm font-medium text-gray-700"
                            }, _toDisplayString(_unref(displayFilterRule)(filterRule)), 9, _hoisted_7)
                          ]),
                          (isBetweenRule.value && !isNotCompareableAttribute.value && localFilter.value.rule === filterRule)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                _createElementVNode("div", _hoisted_9, [
                                  (isDateTypeFilter.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createVNode(GgmsCalendar, {
                                          modelValue: localFilterStart.value,
                                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localFilterStart).value = $event)),
                                          position: "below",
                                          showTime: showTime.value,
                                          showDate: true
                                        }, null, 8, ["modelValue", "showTime"]),
                                        _createVNode(GgmsCalendar, {
                                          modelValue: localFilterEnd.value,
                                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((localFilterEnd).value = $event)),
                                          position: "below",
                                          showTime: showTime.value,
                                          showDate: true
                                        }, null, 8, ["modelValue", "showTime"])
                                      ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createVNode(GgmsInput, {
                                          modelValue: localFilter.value.value[0],
                                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((localFilter.value.value[0]) = $event)),
                                          type: localFilterInputType.value,
                                          isAvailableDateFromPast: true,
                                          class: "w-full"
                                        }, null, 8, ["modelValue", "type"]),
                                        _createVNode(GgmsInput, {
                                          modelValue: localFilter.value.value[1],
                                          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((localFilter.value.value[1]) = $event)),
                                          type: localFilterInputType.value,
                                          isAvailableDateFromPast: true,
                                          class: "w-full"
                                        }, null, 8, ["modelValue", "type"])
                                      ], 64))
                                ])
                              ]))
                            : (!isNotCompareableAttribute.value && localFilter.value.rule === filterRule)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                  (isDateTypeFilter.value)
                                    ? (_openBlock(), _createBlock(GgmsCalendar, {
                                        key: 0,
                                        modelValue: localFilter.value.value,
                                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((localFilter.value.value) = $event)),
                                        position: "below",
                                        showTime: showTime.value,
                                        showDate: true
                                      }, null, 8, ["modelValue", "showTime"]))
                                    : (_openBlock(), _createBlock(GgmsInput, {
                                        key: 1,
                                        modelValue: localFilter.value.value,
                                        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((localFilter.value.value) = $event)),
                                        type: localFilterInputType.value,
                                        isAvailableDateFromPast: true,
                                        class: "w-full"
                                      }, null, 8, ["modelValue", "type"]))
                                ]))
                              : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    : (localFilter.value.field === 'lists._id')
                      ? (_openBlock(), _createBlock(FilterCollection, {
                          key: 7,
                          collectionsSelectedState: collectionsSelectedState.value,
                          hasFilterCollections: hasFilter.value,
                          typeName: collectionType.value,
                          onOnCollectionSelect: onCollectionSelect
                        }, null, 8, ["collectionsSelectedState", "hasFilterCollections", "typeName"]))
                      : (['origins', 'origins._id'].includes(localFilter.value.field))
                        ? (_openBlock(), _createBlock(FilterOrigin, {
                            key: 8,
                            originsSelectedState: originsSelectedState.value,
                            hasFilterOrigins: hasFilter.value,
                            typeName: collectionType.value,
                            onOnOriginSelect: onOriginSelect
                          }, null, 8, ["originsSelectedState", "hasFilterOrigins", "typeName"]))
                        : (_openBlock(), _createBlock(FilterTag, {
                            key: 9,
                            tagsSelectedState: tagsSelectedState.value,
                            hasFilterTags: hasFilter.value,
                            typeName: collectionType.value,
                            onOnTagSelect: onTagSelect
                          }, null, 8, ["tagsSelectedState", "hasFilterTags", "typeName"])),
      (_ctx.showButton)
        ? (_openBlock(), _createBlock(GgmsButton, {
            key: 10,
            class: "w-fit",
            disabled: !isFilterValid.value,
            small: false,
            onClick: _withModifiers(applyFilter, ["stop"])
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(filterButtonText.value), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})